// The platform copy of MK (spec section 6.8): the raw key, base64, keyed by
// member id, placed where the platform will carry it to a restored or replaced
// phone (iCloud Keychain on iOS, Auto Backup on Android). This module is the
// only place that knows which plugin sits underneath, behind
// `put(memberId, copy, slot)`, `get(memberId, slot)`, `remove(memberId, slot)`,
// `isEligible()` and `probe()`. Each member has two slots (`PLATFORM_COPY_SLOT`,
// platform-copy.js says why): the committed one hydration reads, and the
// provisional one an enrolment attempt writes before its POST. There is
// deliberately no `isSynced()`: neither platform can tell an app that a backup
// ran (decision 17). The web build has no platform copy at all; it is eligible
// only under the test-only `courses-notes-web` flag (section 6.10). One
// adapter is handed out per app, so a probe result is not lost between the
// store's calls.
import { useFeatureFlags } from '@/composables/useFeatureFlags';
import { COURSES_NOTES_WEB_FLAG } from '@/constants/courses';
import { isAndroidNativeOnly, isIosNativeOnly } from '@/utils/platform-check';
import { createAndroidPlatformAdapter } from './platform-key-adapter-android';
import { createIosPlatformAdapter } from './platform-key-adapter-ios';

export {
  PLATFORM_COPY_KEY_PREFIX,
  PLATFORM_COPY_PROBE_KEY,
  PLATFORM_COPY_SLOT,
  PLATFORM_COPY_VERSION,
  PENDING_COPY_KEY_PREFIX,
  PROVISIONAL_COPY_KEY_PREFIX,
  isPlatformTombstone,
  parsePlatformCopy,
  platformCopyKey,
  serialisePlatformCopy,
  serialisePlatformTombstone,
} from './platform-copy';
export { COURSES_NOTES_WEB_FLAG };

/**
 * @typedef {object} PlatformKeyAdapter
 * @property {'web' | 'ios' | 'android'} kind
 * @property {() => boolean} isEligible whether this platform can carry the key, as of the last probe
 * @property {() => Promise<boolean>} probe re-checks eligibility and returns it
 * @property {(memberId: number, copy: string, slot?: string) => Promise<void>} put
 * @property {(memberId: number, slot?: string) => Promise<string | null>} get
 * @property {(memberId: number, slot?: string) => Promise<void>} remove
 */

/**
 * @returns {PlatformKeyAdapter}
 */
export function createWebPlatformAdapter() {
  const { isEnabled } = useFeatureFlags();
  const isEligible = () => isEnabled(COURSES_NOTES_WEB_FLAG);
  return {
    kind: 'web',
    isEligible,
    async probe() {
      return isEligible();
    },
    async put() {},
    async get() {
      return null;
    },
    async remove() {},
  };
}

let adapter = null;

/**
 * @returns {PlatformKeyAdapter}
 */
function createPlatformAdapter() {
  if (isIosNativeOnly()) return createIosPlatformAdapter();
  if (isAndroidNativeOnly()) return createAndroidPlatformAdapter();
  return createWebPlatformAdapter();
}

/**
 * @returns {PlatformKeyAdapter} the app's one adapter for this platform
 */
export function resolvePlatformAdapter() {
  if (!adapter) adapter = createPlatformAdapter();
  return adapter;
}

/** Specs switch platforms between cases; the app never calls this. */
export function resetPlatformAdapterForTests() {
  adapter = null;
}
